import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader } from 'lucide-react'
import { use, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { Navigate, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { AuthContext } from '@/components/auth/context'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { REQUEST_RESET_PASSWORD } from '@/config/recaptcha'
import { gql } from '@/lib/graphql/gql'
import { useRecaptcha } from '@/lib/hooks'
import { SevallaLogo } from '@/logos'
import type {
  RequestResetPasswordMutation,
  RequestResetPasswordMutationVariables,
} from '@/routes/login/request-reset-password/index.types.gen'

const REQUEST_RESET_PASSWORD_MUTATION = gql`
  mutation RequestResetPassword($email: String!, $recaptchaToken: String!) {
    requestResetPassword(email: $email, recaptchaToken: $recaptchaToken)
  }
`

const schema = z.object({
  email: z
    .email({ message: 'Email is not valid' })
    .min(1, { message: 'Email is required' }),
})

export const RequestResetPassword = () => {
  const [isPending, startTransition] = useTransition()
  const navigate = useNavigate()
  const { isAuthenticated = false } = use(AuthContext) ?? {}

  const getRecaptchaToken = useRecaptcha()
  const [requestResetPassword] = useMutation<
    RequestResetPasswordMutation,
    RequestResetPasswordMutationVariables
  >(REQUEST_RESET_PASSWORD_MUTATION)

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
    },
  })

  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }

  const onSubmit = async (data: z.infer<typeof schema>) => {
    const isValid = await form.trigger()
    if (isPending || !isValid) {
      return
    }

    startTransition(async () => {
      try {
        const recaptchaToken = await getRecaptchaToken(REQUEST_RESET_PASSWORD)
        await requestResetPassword({
          variables: {
            email: data.email,
            recaptchaToken,
          },
        })
        toast(
          <ToasterContent variant="success">
            Password reset requested successfully
          </ToasterContent>,
          { position: 'top-right' }
        )
      } catch {
        toast(
          <ToasterContent variant="error">
            Failed to request password reset
          </ToasterContent>,
          { position: 'top-right' }
        )
      }
    })
  }

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-4">
        <a
          href="https://sevalla.com"
          className="flex items-center gap-2 self-center"
        >
          <SevallaLogo className="w-24 h-auto" />
        </a>
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="font-display text-xl">
                Reset your password
              </CardTitle>
              <CardDescription>
                You'll receive a link to reset your password, if an account with
                your email exists.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input
                            autoComplete="off"
                            aria-label="Email"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex items-center justify-center gap-4">
                    <Button
                      variant="outline"
                      type="button"
                      disabled={isPending}
                      onClick={() =>
                        navigate({ pathname: '/login' }, { replace: false })
                      }
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1"
                      disabled={isPending}
                      onClick={form.handleSubmit(onSubmit)}
                    >
                      {isPending ? <Loader className="animate-spin" /> : null}
                      Request reset password link
                    </Button>
                  </div>
                </form>
              </Form>
              <div className="mt-4 text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4">
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="https://policies.google.com/terms" target="_blank">
                  Terms of Service
                </a>{' '}
                apply.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
